// Writing legal acceptances. Split from ./legal.ts so the rules there stay
// importable without dragging in the Supabase client.

import { supabase } from "@/lib/supabase";
import {
  LEGAL_DOCUMENT_VERSIONS,
  PENDING_ACCEPTANCE_KEY,
  buildPendingAcceptance,
  parsePendingAcceptance,
  pendingAcceptanceApplies,
  serialisePendingAcceptance,
  type LegalDocument,
} from "@/lib/legal";

/**
 * Records one document version. The RPC is `on conflict do nothing`, so
 * calling it twice is harmless — which matters, because the flush below can
 * fire on several auth events in a row.
 *
 * Never throws: failing to record a tick must not block a signup.
 */
async function recordOne(document: LegalDocument, version: string): Promise<void> {
  const { error } = await supabase.rpc("record_legal_acceptance", {
    p_document: document,
    p_version: version,
  });
  if (error) {
    console.warn(`[legal] could not record ${document} acceptance:`, error.message);
  }
}

/** Records the current version of every document. Never throws. */
export async function recordLegalAcceptance(
  versions: Record<LegalDocument, string> = LEGAL_DOCUMENT_VERSIONS,
): Promise<void> {
  try {
    await Promise.all(
      (Object.entries(versions) as Array<[LegalDocument, string]>).map(([doc, version]) =>
        recordOne(doc, version),
      ),
    );
  } catch (error) {
    console.warn("[legal] acceptance recording failed:", error);
  }
}

/** Parks the signup-time tick until there is a session to attach it to. */
export function stashPendingAcceptance(email: string): void {
  try {
    localStorage.setItem(
      PENDING_ACCEPTANCE_KEY,
      serialisePendingAcceptance(buildPendingAcceptance(email, new Date())),
    );
  } catch {
    // Private-mode localStorage throws. The acceptance is still recorded
    // directly whenever signup returns a session, which is the common path.
  }
}

/**
 * Called on every sign-in. If the signed-in user is the one who ticked the box
 * at signup, the acceptance is written now and the parked copy cleared.
 */
export async function flushPendingLegalAcceptance(
  sessionEmail: string | null | undefined,
): Promise<void> {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_ACCEPTANCE_KEY);
  } catch {
    return;
  }
  const pending = parsePendingAcceptance(raw, new Date());
  if (!pendingAcceptanceApplies(pending, sessionEmail)) {
    // Either nothing parked, or it belongs to a different account. Clear a
    // stale or foreign entry so it cannot be recorded later by accident.
    if (raw && !pending) safeClearPendingAcceptance();
    return;
  }
  await recordLegalAcceptance(pending!.versions);
  safeClearPendingAcceptance();
}

function safeClearPendingAcceptance(): void {
  try {
    localStorage.removeItem(PENDING_ACCEPTANCE_KEY);
  } catch {
    // Nothing to do — a stale entry expires on its own after 30 days.
  }
}
